import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { pushEvent } from '@/hooks/useAnalytics';
import { downloadImage, nativeShareImage } from '@/utils/nativeShare';
import { fetchImageProxy, getGeneration, toggleFavorite } from '@/api/generation';
import { saveGenerationImageToAlbum } from '@/utils/saveGenerationImage';
import type { GenerationStatus, ReasonCode } from '@/api/generation';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  type: 'text' | 'image' | 'loading' | 'system' | 'task';
  content?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  genId?: string;
  clientRequestId?: string;
  referenceImages?: string[];
  styleName?: string;
  ratio?: string;
  prompt?: string;
  status?: GenerationStatus;
  reasonCode?: ReasonCode;
  pointsState?: 'reserved' | 'captured' | 'refunded';
  favorited?: boolean;
}

interface ChatMessageItemProps {
  message: ChatMessage;
  onRegenerate?: () => void;
  isLast?: boolean;
  user?: { isVip?: boolean } | null;
}

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

export default function ChatMessageItem({ message, onRegenerate, isLast, user }: ChatMessageItemProps) {
  const { t } = useTranslation();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [showHdTip, setShowHdTip] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSlow, setImageSlow] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageRetryNonce, setImageRetryNonce] = useState(0);
  const [displayImageUrl, setDisplayImageUrl] = useState(message.thumbnailUrl || message.imageUrl || '');
  const [isFavorited, setIsFavorited] = useState(Boolean(message.favorited));
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [favoriteError, setFavoriteError] = useState<string | null>(null);

  useEffect(() => {
    setIsFavorited(Boolean(message.favorited));
  }, [message.favorited]);

  useEffect(() => {
    if (message.type !== 'image' || !message.imageUrl) return;
    const previewUrl = message.thumbnailUrl || message.imageUrl;
    let cancelled = false;

    setDisplayImageUrl(previewUrl);
    setImageLoaded(false);
    setImageSlow(false);
    setImageError(false);

    if (message.thumbnailUrl && message.thumbnailUrl !== message.imageUrl) {
      const img = new Image();
      img.onload = () => {
        if (!cancelled) setDisplayImageUrl(message.imageUrl || '');
      };
      img.src = message.imageUrl;
    }

    return () => { cancelled = true; };
  }, [message.imageUrl, message.thumbnailUrl, message.type]);

  useEffect(() => {
    if (message.type !== 'image' || imageLoaded || imageError) return;
    const timer = window.setTimeout(() => setImageSlow(true), 5000);
    return () => window.clearTimeout(timer);
  }, [displayImageUrl, imageError, imageLoaded, imageRetryNonce, message.type]);

  if (message.type === 'system') {
    return (
      <div className="flex justify-center py-3">
        <span className="text-[11px] text-gray-400 font-medium tracking-wide">{message.content}</span>
      </div>
    );
  }

  if (message.type === 'loading') {
    return (
      <div className="flex items-end gap-2.5 mb-5 animate-fade-in pl-1">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
        >
          <i className="ri-magic-line text-white text-sm" />
        </div>
        <div
          className="px-4 py-3 rounded-2xl rounded-tl-sm bg-white max-w-[240px]"
          style={{ border: '1px solid rgba(0,0,0,0.04)' }}
        >
          <div className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full bg-gray-300 animate-bounce"
              style={{ animationDuration: '0.6s', animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 rounded-full bg-gray-300 animate-bounce"
              style={{ animationDuration: '0.6s', animationDelay: '0.15s' }}
            />
            <span
              className="w-2 h-2 rounded-full bg-gray-300 animate-bounce"
              style={{ animationDuration: '0.6s', animationDelay: '0.3s' }}
            />
          </div>
          <p className="mt-2 text-xs leading-relaxed text-gray-500">
            {t('chat_generationPendingTip')}
          </p>
        </div>
      </div>
    );
  }

  if (message.type === 'task') {
    const failed = ['failed', 'blocked', 'timeout'].includes(message.status ?? '');
    return (
      <div
        className="mb-5 ml-11 rounded-2xl bg-white px-4 py-3 text-sm shadow-sm"
        data-testid={message.genId ? `generation-task-${message.genId}` : undefined}
        aria-label={message.genId
          ? `generation-task-${message.genId}-${message.status ?? 'unknown'}-${message.pointsState ?? 'unknown'}-${message.reasonCode ?? 'none'}`
          : undefined}
      >
        <div className="flex items-center gap-2 font-medium text-gray-800">
          <i className={failed ? 'ri-error-warning-line text-red-500' : 'ri-loader-4-line animate-spin text-purple-500'} />
          {failed ? t('gen_generationFailed', '生成未完成') : t('chat_generationPendingTip')}
        </div>
        {failed ? (
          <p className="mt-2 text-xs text-gray-500">
            {t(`gen_reason_${message.reasonCode ?? 'default'}`)}
            {message.pointsState === 'refunded' ? ` · ${t('gen_pointsRefunded', '积分已退回')}` : ''}
          </p>
        ) : null}
      </div>
    );
  }

  if (message.type === 'text') {
    const isUser = message.role === 'user';
    return (
      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 animate-fade-in`}>
        {isUser ? (
          <div
            className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-tr-sm text-white text-sm leading-relaxed"
            style={{ background: 'linear-gradient(135deg, #FF6B9D, #C084FC)' }}
          >
            {!!message.referenceImages?.length && (
              <div className="mb-2 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.min(message.referenceImages.length, 3)}, minmax(0, 1fr))` }}>
                {message.referenceImages.map((imageUrl, index) => (
                  <div key={`${imageUrl}_${index}`} className="aspect-square rounded-xl overflow-hidden bg-white/20">
                    <img src={imageUrl} alt={`reference ${index + 1}`} className="w-full h-full object-contain" />
                  </div>
                ))}
              </div>
            )}
            {message.content}
          </div>
        ) : (
          <div className="flex items-start gap-2.5 max-w-[88%] pl-1">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
            >
              <i className="ri-magic-line text-white text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-400 mb-1 ml-1">{t('chat_aiAssistant', 'AI绘画助手')}</p>
              <div
                className="px-4 py-2.5 rounded-2xl rounded-tl-sm bg-white text-sm text-gray-800 leading-relaxed inline-block"
                style={{ border: '1px solid rgba(0,0,0,0.04)' }}
              >
                {message.content}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (message.type === 'image' && message.imageUrl) {
    const handleFavorite = async () => {
      if (!message.genId || favoriteLoading) return;
      const next = !isFavorited;
      setIsFavorited(next);
      setFavoriteLoading(true);
      setFavoriteError(null);
      try {
        const response = await toggleFavorite(message.genId, next);
        setIsFavorited(response.favorited);
        pushEvent('chat_favorite_toggle', undefined, {
          genId: message.genId,
          favorited: response.favorited,
        });
      } catch {
        setIsFavorited(!next);
        setFavoriteError(t('result_favoriteFailed', '收藏失败，请重试'));
      } finally {
        setFavoriteLoading(false);
      }
    };

    const retryImageLoad = async () => {
      setImageLoaded(false);
      setImageSlow(false);
      setImageError(false);
      if (message.genId) {
        try {
          const refreshed = await getGeneration(message.genId);
          if (refreshed.image_url) {
            const previewUrl = refreshed.thumbnail_url || refreshed.image_url;
            setDisplayImageUrl(previewUrl);
            if (refreshed.thumbnail_url && refreshed.thumbnail_url !== refreshed.image_url) {
              const img = new Image();
              img.onload = () => setDisplayImageUrl(refreshed.image_url || '');
              img.src = refreshed.image_url;
            }
          }
        } catch {
          // Fall back to retrying the current image URL.
        }
      }
      setImageRetryNonce((value) => value + 1);
    };

    const handleDownload = async (isHd: boolean) => {
      if (isHd && !user?.isVip) {
        setShowHdTip(true);
        return;
      }
      setSaveStatus('saving');
      const fileName = `glowpic_${Date.now()}.jpg`;
      const doSaveBlob = async (blob: Blob) => {
        const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: t('appName') });
          return;
        }
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      };
      try {
        if (message.genId) {
          const blob = await saveGenerationImageToAlbum({
            genId: message.genId,
            imageUrl: message.imageUrl || displayImageUrl || '',
            quality: isHd ? 'hd' : 'sd',
          });
          if (!(Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios')) {
            await doSaveBlob(blob);
          }
          setSaveStatus('success');
        } else if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
          const blob = await fetchImageProxy(message.imageUrl || displayImageUrl || '');
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          await window.__NATIVE_BRIDGE__?.saveImage?.(base64);
          setSaveStatus('success');
        } else {
          const shared = await nativeShareImage({
            imageUrl: message.imageUrl || displayImageUrl || '',
            fileName,
            prompt: message.prompt || '',
            title: t('appName'),
          });
          if (!shared) {
            await downloadImage(message.imageUrl || displayImageUrl || '', fileName);
          }
          setSaveStatus('success');
        }
      } catch {
        setSaveStatus('error');
      }
      setTimeout(() => setSaveStatus('idle'), 2000);
    };

    return (
      <div className="flex items-start gap-2.5 mb-5 animate-fade-in pl-1">
        <button
          type="button"
          onClick={() => { void handleFavorite(); }}
          disabled={!message.genId || favoriteLoading}
          data-testid={message.genId ? `generation-favorite-${message.genId}` : undefined}
          aria-label={isFavorited ? t('result_favorited') : t('result_addFavorite')}
          aria-pressed={isFavorited}
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 cursor-pointer active:scale-90 transition-transform disabled:cursor-not-allowed disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
        >
          <i className={`${favoriteLoading ? 'ri-loader-4-line animate-spin' : isFavorited ? 'ri-star-fill' : 'ri-star-line'} text-white text-sm`} />
        </button>
        <div className="flex-1 max-w-[88%]">
          <div
            role="status"
            data-testid={message.genId ? `generation-result-${message.genId}` : undefined}
            data-generation-id={message.genId ?? ''}
            aria-label={message.genId
              ? `generation-result-${message.genId}-${imageLoaded ? 'loaded' : 'loading'}`
              : 'generation-result'}
            className="rounded-2xl rounded-tl-sm overflow-hidden bg-white"
            style={{ border: '1px solid rgba(0,0,0,0.04)' }}
          >
            <div className="relative bg-gray-50">
              {!imageLoaded && (
                <div className="min-h-[320px] flex flex-col items-center justify-center px-5 text-center">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #FFE4EF, #F3E8FF)' }}>
                    {imageError ? (
                      <i className="ri-image-close-line text-pink-500 text-base" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
                    )}
                  </div>
                  <p className="text-xs font-semibold text-gray-700">
                    {imageError ? '图片加载失败' : imageSlow ? '生成完成，正在加载高清图' : '正在加载图片'}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
                    {imageError ? '网络不稳定时可以重新加载图片。' : '复杂图片体积较大，首次展示可能需要多等一会儿。'}
                  </p>
                  {(imageSlow || imageError) && (
                    <button
                      type="button"
                      onClick={() => { void retryImageLoad(); }}
                      className="mt-3 px-3 py-1.5 rounded-full bg-pink-50 text-pink-500 text-[11px] font-medium active:bg-pink-100"
                    >
                      重新加载
                    </button>
                  )}
                </div>
              )}
              <img
                key={`${displayImageUrl}_${imageRetryNonce}`}
                src={displayImageUrl}
                alt="AI generated"
                aria-label={message.genId ? `generation-image-${message.genId}` : 'generation-image'}
                className="block max-w-full max-h-[60vh] mx-auto object-contain"
                style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
                onLoad={() => {
                  setImageLoaded(true);
                  setImageSlow(false);
                  setImageError(false);
                }}
                onError={() => {
                  setImageError(true);
                  setImageSlow(false);
                }}
              />
            </div>

            <div
              data-testid="chat-result-metadata"
              className="px-3 pt-2 pb-1 flex items-center gap-2 flex-wrap"
            >
              {message.styleName && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-pink-50 text-pink-500 font-medium">
                  {message.styleName}
                </span>
              )}
              {message.ratio && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-purple-50 text-purple-500 font-medium">
                  {message.ratio}
                </span>
              )}
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-50 text-amber-500 font-medium">
                {t('gen_success')}
              </span>
            </div>

            <div
              data-testid="chat-result-actions"
              className="mt-2 px-3 pb-3 grid grid-cols-3 gap-1.5"
            >
              <button
                onClick={() => handleDownload(false)}
                data-testid="generation-download-sd"
                aria-label="generation-download-sd"
                className="min-w-0 h-9 px-1 rounded-xl bg-gray-50 text-gray-600 text-[11px] font-medium flex items-center justify-center gap-1 active:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-line text-xs" />
                <span>{t('gen_sdDownload')}</span>
              </button>
              <button
                onClick={() => handleDownload(true)}
                className="min-w-0 h-9 px-1 rounded-xl text-[11px] font-medium flex items-center justify-center gap-1 active:opacity-80 transition-opacity cursor-pointer whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #FF6B9D15, #A855F715)', color: '#A855F7' }}
              >
                <i className="ri-vip-crown-line text-xs" />
                <span>{t('gen_hdDownload')}</span>
              </button>
              <button
                onClick={() => { pushEvent('chat_regenerate_click'); onRegenerate?.(); }}
                className="min-w-0 h-9 px-1 rounded-xl text-white text-[11px] font-medium flex items-center justify-center gap-1 active:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              >
                <i className="ri-refresh-line text-xs" />
                <span>{t('gen_regenerate')}</span>
              </button>
            </div>
          </div>

          {message.prompt && (
            <div className="mt-2 px-3 py-2 rounded-xl bg-gray-50/80">
              <p className="text-[11px] text-gray-400 leading-relaxed line-clamp-2">{message.prompt}</p>
            </div>
          )}

          {isLast && onRegenerate && (
            <button
              onClick={() => { pushEvent('chat_regenerate_click'); onRegenerate(); }}
              className="mt-3 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-medium text-pink-500 bg-pink-50 active:bg-pink-100 transition-colors cursor-pointer whitespace-nowrap w-fit"
            >
              <i className="ri-refresh-line" />
              <span>{t('gen_regenerate')}</span>
            </button>
          )}

          {saveStatus !== 'idle' && (
            <div
              role="status"
              data-testid={`image-save-${saveStatus}`}
              aria-label={`image-save-${saveStatus}${message.genId ? `-${message.genId}` : ''}`}
              className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900/5 w-fit"
            >
              {saveStatus === 'saving' && (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  <span className="text-[11px] text-gray-500">{t('gen_saving')}</span>
                </>
              )}
              {saveStatus === 'success' && (
                <>
                  <i className="ri-check-line text-green-500 text-sm" />
                  <span className="text-[11px] text-gray-600">{t('gen_saveSuccess')}</span>
                </>
              )}
              {saveStatus === 'error' && (
                <>
                  <i className="ri-close-line text-red-400 text-sm" />
                  <span className="text-[11px] text-gray-600">保存失败</span>
                </>
              )}
            </div>
          )}
          {favoriteError && (
            <div role="alert" className="mt-2 flex items-center gap-1 px-3 py-2 rounded-xl bg-red-50 text-[11px] text-red-500 w-fit">
              <i className="ri-error-warning-line" />
              <span>{favoriteError}</span>
            </div>
          )}
        </div>

        {showHdTip && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-5">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowHdTip(false)} />
            <div className="relative bg-white rounded-3xl p-6 w-full max-w-xs animate-scale-in">
              <div
                className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FFE4EF, #F3E8FF)' }}
              >
                <i className="ri-vip-crown-fill text-pink-500 text-xl" />
              </div>
              <h3 className="text-base font-bold text-gray-800 text-center mb-1">{t('gen_hdNeedVip')}</h3>
              <p className="text-xs text-gray-500 text-center mb-4">{t('gen_hdNeedVipDesc')}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowHdTip(false)}
                  className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-medium text-xs cursor-pointer whitespace-nowrap"
                >
                  {t('gen_cancel')}
                </button>
                <button
                  onClick={() => setShowHdTip(false)}
                  className="flex-1 py-2.5 rounded-xl text-white font-medium text-xs cursor-pointer whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
                >
                  {t('gen_openMembership')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
